import invariant from 'tiny-invariant';
import { PAYMENT_STATUS, PaymentInfo, StripeInfo } from '@type/db-types';
import { supabase } from './supabase-config';
import { breadcrumb } from '@utils/logging';
import {
    REALTIME_POSTGRES_CHANGES_LISTEN_EVENT,
    RealtimePostgresChangesPayload,
} from '@supabase/supabase-js';

const PAYMENTS = 'payments';
const STRIPE = 'stripe';

export const getStripeInfo = async (userId: string): Promise<StripeInfo | null> => {
    const { data, error } = await supabase.from(STRIPE).select('*').eq('id', userId).single();

    if (error) console.log(`stripe account not found: ${error.message}`);

    breadcrumb('getPaymentSetupStatus', { data, userId });

    return data;
};

export function listenToStripeAccountChanges(
    userId: string,
    setStripeAccount: (message: StripeInfo) => void,
): () => void {
    invariant(userId, 'User must be signed in to get stripe setup status');

    const onCallback = async (payload: RealtimePostgresChangesPayload<StripeInfo>) => {
        // console.log(`StripeInfo listener updated: `, payload);
        if (REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.DELETE === payload.eventType) {
            breadcrumb('listenToStripeAccountChanges has no new payload', { payload });
        } else {
            setStripeAccount(payload.new);
        }
    };

    // Listen to inserts on the Stripe table for the given userId
    const insertQuery = supabase
        .channel('listenToStripeAccountChanges')
        .on<StripeInfo>(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: STRIPE,
                filter: `id=eq.${userId}`,
            },
            onCallback,
        )
        .subscribe();

    return () => insertQuery.unsubscribe();
}

// Get the payment info for the given paymentId
export async function getPaymentInfoByPaymentId(paymentId: string): Promise<PaymentInfo | null> {
    if (!paymentId) {
        console.error('getPaymentInfo: missing userId or paymentId', { paymentId });
        return Promise.resolve(null);
    }

    const { data, error } = await supabase.from(PAYMENTS).select('*').eq('id', paymentId).single();

    if (error) {
        console.error('getPaymentInfo: error fetching payment info', {
            error,
            paymentId,
        });
        return null;
    }

    return data;
}

// Get the payment info for the given sessionId
export async function getPaymentInfoBySessionId(sessionId: string): Promise<PaymentInfo | null> {
    if (!sessionId) {
        console.error('getPaymentInfo: missing userId or sessionId', {});
        return Promise.resolve(null);
    }

    const { data, error } = await supabase
        .from(PAYMENTS)
        .select('*')
        .eq('stripeSessionId', sessionId)
        .single();

    if (error) {
        console.error('getPaymentInfo: error fetching payment info', {
            error,
            stripeSessionId: sessionId,
        });
        return null;
    }

    return data;
}

// Listen to changes in the payments table for the given sessionId
export function listenToPaymentChanges({
    onUpdate,
    ...params
}:
    | { sessionId: string; onUpdate: (message: PaymentInfo) => void }
    | { paymentId: string; onUpdate: (message: PaymentInfo) => void }): () => void {
    if (!('sessionId' in params) && !('paymentId' in params)) {
        console.error('listenToPaymentChanges: missing paymentId or sessionId', params);
        return () => {};
    }

    const onCallback = async (payload: RealtimePostgresChangesPayload<PaymentInfo>) => {
        console.log(`PaymentInfo listener updated: `, payload);
        if (REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.DELETE === payload.eventType) {
            breadcrumb('listenToPaymentChanges has no new payload', { payload });
        } else {
            onUpdate(payload.new);
        }
    };

    console.log(`[listenToPaymentChanges] started...`, { params });

    // Listen to inserts on the Stripe table for the given userId
    const insertQuery = supabase
        .channel('listenToPaymentChanges')
        .on<PaymentInfo>(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: PAYMENTS,
                filter:
                    'sessionId' in params
                        ? `stripeSessionId=eq.${params.sessionId}`
                        : `id=eq.${params.paymentId}`,
            },
            onCallback,
        )
        .subscribe();

    return () => insertQuery.unsubscribe();
}

export function updateRegistrantPaymentStatus(
    paymentStatus: PAYMENT_STATUS | null,
    paymentId: string,
) {
    invariant(
        paymentStatus && paymentId,
        '[updateRegistrantPaymentStatus] paymentId and paymentStatus required',
    );
    // console.log(`updateRegistrantPaymentStatus`, { divisionId, classId, paymentId });
    return supabase.from(PAYMENTS).update({ status: paymentStatus }).eq('id', paymentId);
}
